import Bugsnag from '@bugsnag/js'
import { Close, Search } from '@mui/icons-material'
import { Box, Icon, Typography } from '@mui/material'
import AdxButton from 'components/custom/AdxButton'
import AdxCheckbox from 'components/custom/AdxCheckbox'
import AdxFormControlLabel from 'components/custom/AdxFormControlLabel'
import AdxInput from 'components/custom/AdxInput'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import AdxTabsBadge from 'components/reusable/tab/AdxTabsBadge'
import AdxTabsPanel from 'components/reusable/tab/AdxTabsPanel'
import { Field, FieldArray, Form, Formik } from 'formik'
import EmployeeList from 'module/payroll/global-components/EmployeeList'
import React, { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { excludeSelectedEmployee } from 'services/api/modules/payroll/payroll-config/excludeEmployeeApi'

const ExcludedEmployeeModal = ({ employees, children }) => {
  const [isModalOpen, setModalOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleClose = () => {
    setModalOpen(false)
  }

  const queryClient = useQueryClient()
  const { mutate } = useMutation((payload) => excludeSelectedEmployee(payload), {
    onSuccess: () => {
      setIsLoading(false)
      setModalOpen(false)
      queryClient.invalidateQueries('getExcludedEmployees')
      queryClient.invalidateQueries('getAllAvailableEmployees')
    },
    onError: (error) => {
      setIsLoading(false)
      Bugsnag.notify(error.data)
    },
  })
  const handleSubmission = (values) => {
    setIsLoading(true)
    mutate(values)
  }
  return (
    <>
      <Box onClick={() => setModalOpen(true)}>{children}</Box>
      <Formik
        enableReinitialize
        initialValues={{
          employee_ids: [],
        }}
        onSubmit={(values) => handleSubmission(values)}
      >
        {({ values, handleSubmit, resetForm }) => {
          return (
            <Form>
              <AdxFormModal
                open={isModalOpen}
                onClose={() => {
                  handleClose()
                  resetForm()
                }}
                handleClose={() => {
                  handleClose()
                  resetForm()
                }}
                childPadding={false}
                title={'Exclude employee'}
                sxModal={{ width: 600 }}
                enableFooter
                actionText={`Exclude (${values.employee_ids.length})`}
                handleAction={handleSubmit}
                isLoading={isLoading}
                actionButtonDisabled={isLoading}
              >
                <Box flexDirection="column" justifyContent="space-between">
                  <FieldArray name="employee_ids">
                    {({ form, push, remove }) => {
                      const { values } = form
                      return (
                        <AdxTabsBadge scrollButtons={false}>
                          <AdxTabsPanel label="All employees">
                            <Box sx={{ p: 2 }}>
                              <AdxInput
                                placeholder="Search for employee to add"
                                onChange={(e) => setSearchTerm(e.target.value)}
                                fullWidth
                                value={searchTerm}
                                sx={{
                                  '& .MuiInputBase-root': {
                                    minHeight: '35px',
                                    outlineColor: '#E5E5E5',
                                  },
                                  '&.MuiBox-root': {
                                    bgcolor: 'white.main',
                                    width: 'auto',
                                  },
                                }}
                                startAdornment={<Search color="light-gray-text" sx={{ mr: 1 }} />}
                                endAdornment={
                                  searchTerm && (
                                    <Close
                                      color="alternate"
                                      onClick={() => setSearchTerm('')}
                                      sx={{ cursor: 'pointer' }}
                                    />
                                  )
                                }
                              />
                              <Box
                                display="flex"
                                flexDirection="column"
                                overflow="auto"
                                sx={{ height: 'calc(100vh - 445px)' }}
                                className="scrollbar"
                              >
                                {employees
                                  ?.filter((item) => {
                                    if (searchTerm === '') {
                                      return item
                                    } else if (
                                      item.name.toLowerCase().includes(searchTerm.toLowerCase())
                                    ) {
                                      return item
                                    } else {
                                      return null
                                    }
                                  })
                                  .map((list, index) => {
                                    return (
                                      <Field
                                        name={`employee_ids.${index}`}
                                        as={AdxFormControlLabel}
                                        onChange={(e) => {
                                          if (
                                            values.employee_ids.find(
                                              (id) => id === parseInt(e.target.value)
                                            )
                                          ) {
                                            remove(
                                              values.employee_ids.findIndex(
                                                (id) => id === parseInt(e.target.value)
                                              )
                                            )
                                          } else {
                                            push(parseInt(e.target.value))
                                          }
                                        }}
                                        type="no-fill"
                                        key={index}
                                        control={<AdxCheckbox value={list?.employee_id} />}
                                        checked={
                                          values.employee_ids.find(
                                            (item) => item === list?.employee_id
                                          )
                                            ? true
                                            : false
                                        }
                                        label={
                                          <EmployeeList
                                            avatar={list.avatar}
                                            sxAvatar={{
                                              width: '30px',
                                              height: '30px',
                                            }}
                                            name={list.name}
                                            position={list.position}
                                          />
                                        }
                                      />
                                    )
                                  })}
                              </Box>
                            </Box>
                          </AdxTabsPanel>
                          <AdxTabsPanel label={`Selected (${values.employee_ids.length})`}>
                            <Box
                              display="flex"
                              flexDirection="column"
                              overflow="auto"
                              sx={{ px: 2, py: 1, height: 'calc(100vh - 320px)' }}
                              className="scrollbar"
                            >
                              {employees
                                ?.filter((item) => {
                                  if (values.employee_ids.find((id) => id === item.employee_id)) {
                                    return item
                                  } else {
                                    return null
                                  }
                                })
                                .map((list, index) => {
                                  return (
                                    <Field
                                      name={`employee_ids.${index}`}
                                      as={AdxFormControlLabel}
                                      type="no-fill"
                                      key={index}
                                      control={<AdxCheckbox />}
                                      checked
                                      onChange={(e) => {
                                        remove(
                                          values.employee_ids.findIndex(
                                            (id) => id === parseInt(e.target.value)
                                          )
                                        )
                                      }}
                                      label={
                                        <EmployeeList
                                          avatar={list.avatar}
                                          sxAvatar={{
                                            width: '30px',
                                            height: '30px',
                                          }}
                                          name={list.name}
                                          position={list.position}
                                        />
                                      }
                                    />
                                  )
                                })}
                            </Box>
                          </AdxTabsPanel>
                        </AdxTabsBadge>
                      )
                    }}
                  </FieldArray>
                  {/* {values.employee_ids.length !== 0 && (
                    <Box
                      sx={{
                        px: 3,
                        py: 2,
                        borderTop: 1,
                        borderColor: 'gray-line.main',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'end',
                      }}
                    >
                      <AdxButton
                        sx={{ minWidth: '200px' }}
                        isLoading={isLoading}
                        disabled={isLoading}
                        onClick={handleSubmit}
                      >{`Exclude (${values.employee_ids.length})`}</AdxButton>
                    </Box>
                  )} */}
                </Box>
              </AdxFormModal>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

export default ExcludedEmployeeModal
